/**
 * LunarDayAstrology — the xemlicham-style "Xem ngày tốt xấu hôm nay" block.
 *
 * UI chrome (section headings, table labels) is translated via i18n.
 * Vietnamese astrology data (lunar data tables in lunarUtils.js — YI_JI_VI,
 * STAR_VI, XIU_VI, PENG_ZU_VI, ZHI_XING, LUC_DIEU, etc.) stays Vietnamese in
 * both VN and EN modes per the strategy decided in docs/20260523-i18n-migration.md.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { getDayAstrology, isVietnameseHoliday, getHolidayName } from '../../utils/lunarUtils';

export default function LunarDayAstrology({ date }) {
  const { t, i18n } = useTranslation('calendar');
  const lang = i18n.language?.startsWith('en') ? 'en' : 'vi';

  if (!date) return null;
  const astro = getDayAstrology(date);
  if (!astro) return null;
  const holiday = isVietnameseHoliday(date);

  // Hoàng/Hắc Đạo pill — append (Lucky)/(Unlucky) in EN mode for non-Vietnamese readers
  const luckPillLabel = (() => {
    if (!astro.luckLabel) return null;
    if (lang !== 'en') return astro.luckLabel;
    const suffix = astro.luck === 'hoang_dao'
      ? t('astro.luckSuffix.lucky')
      : t('astro.luckSuffix.unlucky');
    return `${astro.luckLabel} (${suffix})`;
  })();

  return (
    <div className="space-y-3 px-4 py-3">
      {/* Header pill + can-chi triple */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <p className="text-xs text-stone-700">
            {t('astro.year')} <strong>{astro.yearGanZhi}</strong> · {t('astro.month')} <strong>{astro.monthGanZhi}</strong> · {t('astro.day')} <strong>{astro.dayGanZhi}</strong>
          </p>
          {astro.luck && (
            <span
              className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold ${
                astro.luck === 'hoang_dao'
                  ? 'bg-red-100 text-red-700 border border-red-200'
                  : 'bg-violet-100 text-violet-700 border border-violet-200'
              }`}
            >
              <span className={`w-2 h-2 rounded-full ${astro.luck === 'hoang_dao' ? 'bg-red-600' : 'bg-violet-600'}`} />
              {luckPillLabel}
            </span>
          )}
        </div>
        {(astro.naYin || astro.chongShengXiao || astro.currentJieQi) && (
          <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
            {astro.naYin && <div><span className="text-stone-500">{t('astro.label.naYin')}:</span> <span className="font-medium text-stone-800">{astro.naYin}</span></div>}
            {astro.chongShengXiao && <div><span className="text-stone-500">{t('astro.label.chong')}:</span> <span className="font-medium text-stone-800">{astro.chongShengXiao} ({astro.chong})</span></div>}
            {astro.currentJieQi && <div><span className="text-stone-500">{t('astro.label.jieQi')}:</span> <span className="font-medium text-stone-800">{astro.currentJieQi}</span></div>}
          </div>
        )}
        {holiday && (
          <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold text-white" style={{ backgroundColor: holiday.color }}>
            {getHolidayName(holiday, lang)}
          </div>
        )}
      </div>

      {/* Việc nên làm / Việc nên tránh */}
      {(astro.yi?.length > 0 || astro.ji?.length > 0) && (
        <div className="grid grid-cols-1 gap-2">
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <h4 className="text-xs font-semibold text-red-700 uppercase mb-1.5">{t('astro.section.yi')}</h4>
            <div className="flex flex-wrap gap-1">
              {astro.yi.length ? astro.yi.map((it, i) => (
                <span key={i} className="px-1.5 py-0.5 bg-white rounded text-[11px] text-red-800 border border-red-100">{it}</span>
              )) : <span className="text-[11px] text-stone-500 italic">—</span>}
            </div>
          </div>
          <div className="bg-violet-50 border border-violet-200 rounded-lg p-3">
            <h4 className="text-xs font-semibold text-violet-700 uppercase mb-1.5">{t('astro.section.ji')}</h4>
            <div className="flex flex-wrap gap-1">
              {astro.ji.length ? astro.ji.map((it, i) => (
                <span key={i} className="px-1.5 py-0.5 bg-white rounded text-[11px] text-violet-800 border border-violet-100">{it}</span>
              )) : <span className="text-[11px] text-stone-500 italic">—</span>}
            </div>
          </div>
        </div>
      )}

      {/* Giờ Hoàng Đạo / Hắc Đạo */}
      {astro.hourBlocks?.length > 0 && (
        <div className="bg-white border border-stone-200 rounded-lg p-3">
          <h4 className="text-xs font-semibold text-stone-700 uppercase mb-2">{t('astro.section.hours')}</h4>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="flex items-center gap-1 mb-1">
                <span className="w-2 h-2 rounded-full bg-red-600" />
                <span className="text-[11px] font-semibold text-red-700">{t('astro.hours.good')}</span>
              </div>
              <div className="space-y-0.5">
                {astro.hourBlocks.filter(h => h.type === 'hoang_dao').map(h => (
                  <div key={h.branch} className="flex justify-between text-[11px] px-1.5 py-0.5 bg-red-50 rounded">
                    <span className="font-semibold text-red-800">{h.branch}</span>
                    <span className="text-stone-600 num">{h.range}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="flex items-center gap-1 mb-1">
                <span className="w-2 h-2 rounded-full bg-violet-600" />
                <span className="text-[11px] font-semibold text-violet-700">{t('astro.hours.bad')}</span>
              </div>
              <div className="space-y-0.5">
                {astro.hourBlocks.filter(h => h.type === 'hac_dao').map(h => (
                  <div key={h.branch} className="flex justify-between text-[11px] px-1.5 py-0.5 bg-violet-50 rounded">
                    <span className="font-semibold text-violet-800">{h.branch}</span>
                    <span className="text-stone-600 num">{h.range}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tam Nương / Nguyệt Kỵ warning */}
      {(astro.isTamNuong || astro.isNguyetKy) && (
        <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 text-xs text-violet-800">
          <h4 className="font-semibold text-violet-700 uppercase mb-1">{t('astro.section.taboo')}</h4>
          {astro.isTamNuong && (
            <p className="mb-1">{t('astro.taboo.tamNuong', { day: astro.lunarDayOfMonth })}</p>
          )}
          {astro.isNguyetKy && (
            <p>{t('astro.taboo.nguyetKy', { day: astro.lunarDayOfMonth })}</p>
          )}
        </div>
      )}

      {/* Ngũ Hành Can-Chi */}
      {astro.canChi && (
        <div className="bg-white border border-stone-200 rounded-lg p-3 text-xs">
          <h4 className="font-semibold text-stone-700 uppercase mb-1">{t('astro.section.element')}</h4>
          <p className="text-stone-700">
            {t('astro.element.summary', { gz: astro.dayGanZhi, stem: astro.dayStemElement, branch: astro.dayBranchElement })}{' '}
            <span className={
              astro.canChi.luck === 'good' ? 'text-red-700 font-semibold' :
              astro.canChi.luck === 'bad'  ? 'text-violet-700 font-semibold' :
                                              'text-amber-700 font-semibold'
            }>{astro.canChi.label}</span>
            . {astro.canChi.desc}
          </p>
        </div>
      )}

      {/* Trực + Khổng Minh Lục Diệu */}
      {(astro.truc || astro.lucDieu) && (
        <div className="grid grid-cols-2 gap-2 text-xs">
          {astro.truc && (
            <div className="bg-white border border-stone-200 rounded-lg p-3">
              <h4 className="font-semibold text-stone-700 uppercase mb-1">{t('astro.section.truc')}</h4>
              <div className="text-base font-bold text-red-700">{astro.truc.name}</div>
              <p className="text-[11px] text-stone-600 mt-0.5">{astro.truc.hint}</p>
            </div>
          )}
          {astro.lucDieu && (
            <div className={'border rounded-lg p-3 ' + (astro.lucDieu.luck === 'good' ? 'bg-red-50 border-red-200' : 'bg-violet-50 border-violet-200')}>
              <h4 className={'font-semibold uppercase mb-1 ' + (astro.lucDieu.luck === 'good' ? 'text-red-700' : 'text-violet-700')}>{t('astro.section.lucDieu')}</h4>
              <div className={'text-base font-bold ' + (astro.lucDieu.luck === 'good' ? 'text-red-800' : 'text-violet-800')}>{astro.lucDieu.name}</div>
              <p className="text-[11px] text-stone-700 mt-0.5">{astro.lucDieu.note}</p>
            </div>
          )}
        </div>
      )}

      {/* Nhị Thập Bát Tú */}
      {astro.xiu && (
        <div className="bg-white border border-stone-200 rounded-lg p-3 text-xs">
          <h4 className="font-semibold text-stone-700 uppercase mb-1">
            {t('astro.section.xiu', { name: astro.xiu })}
          </h4>
          {astro.xiuDesc && (
            <p className="text-stone-700 leading-relaxed">{astro.xiuDesc}</p>
          )}
        </div>
      )}

      {/* Tương quan tuổi */}
      {astro.branchRelations && (
        <div className="bg-white border border-stone-200 rounded-lg p-3 text-xs">
          <h4 className="font-semibold text-stone-700 uppercase mb-2">
            {t('astro.section.branchRel', { branch: astro.branchRelations.branch })}
          </h4>
          <div className="grid grid-cols-3 gap-1.5">
            {astro.branchRelations.lucHop && (
              <div className="text-center p-1.5 bg-red-50 rounded border border-red-100">
                <div className="text-[10px] text-red-700 uppercase">{t('astro.branchRel.lucHop')}</div>
                <div className="font-semibold text-red-900 mt-0.5">{astro.branchRelations.lucHop}</div>
              </div>
            )}
            {astro.branchRelations.tamHop?.length > 0 && (
              <div className="text-center p-1.5 bg-rose-50 rounded border border-rose-100">
                <div className="text-[10px] text-rose-700 uppercase">{t('astro.branchRel.tamHop')}</div>
                <div className="font-semibold text-rose-900 mt-0.5">{astro.branchRelations.tamHop.join(' · ')}</div>
              </div>
            )}
            {astro.branchRelations.xung && (
              <div className="text-center p-1.5 bg-violet-50 rounded border border-violet-100">
                <div className="text-[10px] text-violet-700 uppercase">{t('astro.branchRel.xung')}</div>
                <div className="font-semibold text-violet-900 mt-0.5">{astro.branchRelations.xung}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Hướng xuất hành */}
      {astro.positions && (astro.positions.xi || astro.positions.cai || astro.positions.fu) && (
        <div className="bg-white border border-stone-200 rounded-lg p-3">
          <h4 className="text-xs font-semibold text-stone-700 uppercase mb-2">{t('astro.section.directions')}</h4>
          <div className="grid grid-cols-3 gap-1.5 text-xs">
            {astro.positions.xi && (
              <div className="text-center p-1.5 bg-red-50 rounded border border-red-100">
                <div className="text-[10px] text-red-700 uppercase">{t('astro.direction.xi')}</div>
                <div className="font-semibold text-red-900 mt-0.5">{astro.positions.xi}</div>
              </div>
            )}
            {astro.positions.cai && (
              <div className="text-center p-1.5 bg-amber-50 rounded border border-amber-100">
                <div className="text-[10px] text-amber-700 uppercase">{t('astro.direction.cai')}</div>
                <div className="font-semibold text-amber-900 mt-0.5">{astro.positions.cai}</div>
              </div>
            )}
            {astro.positions.fu && (
              <div className="text-center p-1.5 bg-rose-50 rounded border border-rose-100">
                <div className="text-[10px] text-rose-700 uppercase">{t('astro.direction.fu')}</div>
                <div className="font-semibold text-rose-900 mt-0.5">{astro.positions.fu}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bành Tổ bách kỵ */}
      {(astro.pengZuGan || astro.pengZuZhi) && (
        <div className="bg-white border border-stone-200 rounded-lg p-3 text-xs">
          <h4 className="font-semibold text-stone-700 uppercase mb-1">{t('astro.section.pengZu')}</h4>
          {astro.pengZuGan && <p className="text-stone-700 mb-0.5">{astro.pengZuGan}</p>}
          {astro.pengZuZhi && <p className="text-stone-700">{astro.pengZuZhi}</p>}
        </div>
      )}
    </div>
  );
}
